import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ParsedVideo, Picture, Rotation, UiState, Video } from './types';
import { GlobalState } from '../../../types.global';

import { selectDefaultBackground } from './selectors';
import { setCustomPicture, setRawPicture } from './thunks';
import { browserStorageUpdate } from '../../../store/actions';
import { DEFAULT_PICTURE } from './dict';
import { isSameDay } from '../../../../shared';

export const GalleryCategories = [
  'ocean',
  'space',
  'landscape',
  'forest',
  'city',
  'animals',
  'cats',
  'dogs',
];

export const getInitialUiState = (state: GlobalState): UiState => {
  const defaultBg = selectDefaultBackground(state);
  const result = {
    isDefaultSettings: true,
    mode: 'gallery',
    activeView: 'default',
    ...defaultBg,
    customizeGalleryTried: defaultBg.mode === 'gallery',
    customizeClicked: false,
    customPicture: {
      pictureUrl: null,
      raw: null,
      warning: null,
      uploadState: null,
    },
    nextImage: null,
    ...state.ui,
    videoBackground: {
      url: null,
      show: false,
      ...state.ui.videoBackground,
    },
    gallery: {
      liked: [],
      disliked: [],
      image: { viewCount: 0, picture: DEFAULT_PICTURE, updatedAt: Date.now() },
      category: 'ocean',
      rotation: { type: 'byTabs', value: 2 },
      ...(state.ui || {}).gallery,
    },
    customizeOpened: false,
    dragAndDropOpened: false,
    hideUi: false,
  };

  // always reset it so it's not stuck in pending
  result.customPicture.uploadState = null;

  if (result.mode === 'gallery') {
    // If video background is turned on, update the view count and switch to the next video if loaded
    if (result.videoBackground.show) {
      result.videoBackground = {
        ...result.videoBackground,
        // Increment view count
        viewCount: result.videoBackground?.viewCount
          ? result.videoBackground.viewCount + 1
          : 1,
      };

      // If the current video has been viewed enough times, switch to the next video
      if (
        (result.videoBackground.nextVideo &&
          result.gallery.rotation.type === 'byTabs' &&
          result.videoBackground.viewCount >= result.gallery.rotation.value) ||
        (result.gallery.rotation.type === 'byDays' &&
          result.videoBackground.viewCount >= 1 &&
          !isSameDay(result.videoBackground.updatedAt))
      ) {
        result.videoBackground = {
          ...result.videoBackground,
          ...result.videoBackground.nextVideo,
          updatedAt: Date.now(),
          nextVideo: null,
          viewCount: 1,
        };
      }
    } else {
      result.gallery.image.viewCount++;
      if (result.gallery.nextImage?.status === 'READY') {
        result.gallery.image.viewCount = 1;
        result.gallery.image.picture = result.gallery.nextImage.picture;
        result.gallery.image.updatedAt = Date.now();
        result.gallery.nextImage = null;
      }
    }
  }

  return result;
};

// -----------------------------------------------------------------------------
// SLICE

export const uiSlice = createSlice({
  name: 'ui',
  initialState: null,
  reducers: {
    clickedSupportUs: (state, { payload }: PayloadAction<string>): void => {
      state.modalName = payload;
    },
    selectedNextPicture: (
      state: UiState,
      { payload }: PayloadAction<Picture>
    ): void => {
      state.gallery.nextImage = {
        picture: payload,
        status: 'SELECTED',
      };
    },
    preloadedNextPicture: (state: UiState): void => {
      if (state.gallery.nextImage) {
        state.gallery.nextImage.status = 'READY';
      }
    },

    changedMode: (state: UiState, { payload }): void => {
      state.isDefaultSettings = false;
      state.mode = payload.mode;
      state.gallery.nextImage = null;
    },

    setGallery: (state: UiState, { payload }: PayloadAction<string>): void => {
      state.activeView = `gallery:${payload}`;
    },

    updateRawPictureUrl: (
      state: UiState,
      { payload }: PayloadAction<string>
    ): void => {
      state.customPicture = {
        ...state.customPicture,
        raw: payload,
      };
    },

    resetImageViewCount: (state: UiState): void => {
      state.gallery.image.viewCount = 1;
    },

    updateVideo: (
      state: UiState,
      {
        payload,
      }: PayloadAction<{
        url?: string;
        index?: number;
        credit?: {
          title?: string;
          url?: string;
        };
        show?: boolean;
        id?: number;
        rotation?: Rotation;
      }>
    ): void => {
      if (!state.videoBackground) {
        state.videoBackground = {};
      }

      state.videoBackground = {
        ...state.videoBackground,
        ...payload,
        updatedAt: Date.now(),
      };

      // Set the mode to gallery if it's not already
      if (payload.show && state.mode !== 'gallery') {
        state.mode = 'gallery';
      }

      if (payload.show && !payload.rotation) {
        // Turn off rotation
        state.gallery.rotation = {
          type: null,
          value: null,
          category: null,
        };
      } else if (payload.rotation) {
        // Turn on rotation with new settings
        state.gallery.rotation = payload.rotation;
      }
    },

    setNextVideo: (
      state: UiState,
      { payload }: PayloadAction<ParsedVideo>
    ): void => {
      state.videoBackground = {
        ...state.videoBackground,
        nextVideo: payload,
      };
    },

    selectedCollection: (
      state: UiState,
      {
        payload,
      }: PayloadAction<{
        rotation?: Rotation;
        category: string;
        now: number;
        picture?: Picture;
        mode?: 'gallery' | 'speed' | 'focus';
      }>
    ): void => {
      const { category, picture, rotation, now, mode } = payload;

      state.gallery.nextImage = null;
      state.isDefaultSettings = false;
      state.mode = mode || 'gallery';
      state.customizeGalleryTried = true;
      state.gallery.category = category;
      state.gallery.rotation = rotation || {
        type: null,
        value: null,
        category,
      };

      state.gallery.image.updatedAt = now;
      state.gallery.image.viewCount = 1;
      if (picture) {
        state.gallery.image.picture = picture;
      }
    },
    setDefault: (state: UiState): void => {
      state.activeView = 'default';
      state.dragAndDropOpened = false;
    },
    clickedCustomize: (state: UiState): void => {
      state.activeView = 'default';
      state.customizeClicked = true;
      state.customizeOpened = !state.customizeOpened;
      state.dragAndDropOpened = false;
    },
    openDragDrop: (state: UiState): void => {
      state.activeView = 'custom';
      state.dragAndDropOpened = true;
    },
    toggleUiElements: (
      state: UiState,
      { payload }: PayloadAction<boolean>
    ): void => {
      state.hideUi = payload;
    },
    changedRotation: (
      state: UiState,
      { payload }: PayloadAction<Rotation>
    ): void => {
      state.gallery.nextImage = null;
      state.gallery.rotation = payload || {
        type: null,
        value: null,
        category: null,
      };
    },
    rateBackground: (
      state: UiState,
      {
        payload: { type, isRated },
      }: PayloadAction<{ type: 'liked' | 'disliked'; isRated: boolean }>
    ): void => {
      const { picture } = state.gallery.image;
      if (isRated) {
        const filterType = type === 'liked' ? 'disliked' : 'liked';
        state.gallery[filterType] = state.gallery[filterType].filter(
          (url: string) => url !== picture.url
        );
      }
      state.gallery[type] = [...(state.gallery[type] || []), picture.url];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(browserStorageUpdate, (state, { payload }) => {
      if (payload.isNewer) {
        return getInitialUiState(payload.state);
      }
    });

    builder
      .addCase(setCustomPicture.pending, (state: UiState) => {
        state.customPicture.uploadState = 'loading';
      })
      .addCase(setCustomPicture.rejected, (state: UiState, { error }) => {
        state.customPicture = {
          ...state.customPicture,
          warning: error.message?.match('size') ? 'file_size' : null,
          uploadState: 'error',
        };
      })
      .addCase(
        setRawPicture.fulfilled,
        (
          state: UiState,
          {
            payload: { url, isSmallImage },
          }: PayloadAction<{ url: string; isSmallImage: boolean }>
        ) => {
          state.gallery.nextImage = null;
          state.mode = 'custom';
          state.videoBackground = {
            ...state.videoBackground,
            show: false,
          };
          state.customPicture = {
            pictureUrl: null,
            warning: isSmallImage ? 'small_image' : null,
            raw: url,
            uploadState: null,
          };
          state.activeView = 'default';
        }
      );
  },
});

// -----------------------------------------------------------------------------
// Actions

export const {
  selectedNextPicture,
  preloadedNextPicture,
  setGallery,
  selectedCollection,
  setDefault,
  updateVideo,
  clickedCustomize,
  openDragDrop,
  changedRotation,
  rateBackground,
  clickedSupportUs,
  toggleUiElements,
  changedMode,
  updateRawPictureUrl,
  resetImageViewCount,
  setNextVideo,
} = uiSlice.actions;

export default uiSlice.reducer;
